import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams, useLocation } from 'react-router-dom';
import { Edit, Trash } from 'lucide-react';
import { useNotification } from '../hooks/useNotification';
import { useSyncLanguageUrl } from '../hooks/useSyncLanguageUrl'; // adjust path
import { getImageUrl } from '../utils/getImageUrl';
import BookNavigationTabs from '../components/BookNavigationTabs';

function PreviewBook() {
  const { bookId } = useParams();
  const location = useLocation();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pageLanguage, setPageLanguage] = useState(() => new URLSearchParams(location.search).get('plang') || 'es_de');
  const { notify, Notification } = useNotification();
  const [bookName, setBookName] = useState('');

  useEffect(() => {
    const fetchPages = async () => {
      try {
        const url = bookId ? `/api/list_pages/${bookId}` : `/api/list_pages`;
        const response = await axios.get(url);
        setPages(response.data.pages);
        setBookName(response.data.book_name);
      } catch (err) {
        setError('Failed to fetch pages.');
      } finally {
        setLoading(false);
      }
    };

    if (bookId && pageLanguage) {
      fetchPages();
    }
  }, [bookId, pageLanguage]);

  useSyncLanguageUrl(pageLanguage);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const langParam = searchParams.get('plang');
    if (langParam) {
      setPageLanguage(langParam);
    }
  }, [location.search]);

  const handleRenamePagePrompt = async (pageId, currentName) => {
    const newName = window.prompt('Enter new name for this page:', currentName);

    if (newName === null) return;
    if (!newName.trim()) {
      alert('Page name cannot be empty.');
      return;
    }

    try {
      const response = await axios.post('/api/rename_page', {
        page_id: pageId,
        new_name: newName.trim(),
      });

      if (response.status === 200) {
        notify('Page renamed successfully.');
        const updated = await axios.get(`/api/list_pages/${bookId}`);
        setPages(updated.data.pages);
      }
    } catch (error) {
      console.error('Error renaming page:', error);
      alert('Failed to rename page.');
    }
  };

  const handleDuplicatePage = async (pageId) => {
    try {
      const confirmed = window.confirm(
        'Are you sure you want to duplicate this page?',
      );
      if (!confirmed) return;

      const response = await axios.post(`/api/duplicate_page/${pageId}`);
      if (response.status === 200) {
        notify('Page duplicated successfully.');
        const updated = await axios.get(`/api/list_pages/${bookId}`);
        setPages(updated.data.pages);
      }
    } catch (error) {
      console.error('Error duplicating page:', error);
      alert('Failed to duplicate page.');
    }
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('Are you sure you want to delete this page?')) return;
    try {
      await axios.delete(`/api/delete_page/${pageId}`);
      setPages(pages.filter((page) => page._id !== pageId));
      notify('Page deleted successfully.');
    } catch (err) {
      console.error('Error deleting page:', err);
      alert('Failed to delete page.');
    }
  };

  const handleMovePagePrompt = async (pageId, currentNumber) => {
    const input = window.prompt('Move page to new position:', currentNumber);
    if (input === null) return;
    const targetNumber = parseInt(input);
    if (isNaN(targetNumber) || targetNumber < 0) {
      alert('Invalid number.');
      return;
    }
    try {
      const response = await axios.post(`/api/move_page`, {
        page_id: pageId,
        new_page_number: targetNumber,
      });
      if (response.status === 200) {
        notify('Page moved successfully.');
        const updated = await axios.get(`/api/list_pages/${bookId}`);
        setPages(updated.data.pages);
      }
    } catch (error) {
      console.error('Error moving page:', error);
      alert('Failed to move page.');
    }
  };

  return (
    <>
      <Notification />
      <div className="max-w-[1400px] mx-auto p-6 bg-white shadow-lg rounded-lg mt-10">
        <BookNavigationTabs
          bookId={bookId}
          currentLanguage={pageLanguage}
          headerTitle={bookName || 'Page renders'}
          headerLinkTo={`/books/${bookId}/edit`}
          showBackButton
          showBookSwitcher
          languageSelector={
            <>
              <span className="text-sm text-gray-600">Current Profile:</span>
              <select
                value={pageLanguage}
                onChange={(e) => setPageLanguage(e.target.value)}
                className="border border-gray-300 rounded py-1 px-2 text-sm"
              >
                <option value="es_de">ES-DE</option>
                <option value="es_en">ES-EN</option>
                <option value="es_ru">ES-RU</option>
                <option value="es_it">ES-IT</option>
                <option value="es_tr">ES-TR</option>
                <option value="es_fr">ES-FR</option>
                <option value="es_pl">ES-PL</option>
                <option value="de_nl">DE-NL</option>
                <option value="de_ru">DE-RU</option>
                <option value="de_en">DE-EN</option>
                <option value="de_it">DE-IT</option>
                <option value="de_fr">DE-FR</option>
                <option value="de_tr">DE-TR</option>
                <option value="de_pl">DE-PL</option>
                <option value="en_nl">EN-NL</option>
                <option value="en_pl">EN-PL</option>
                <option value="en_it">EN-IT</option>
                <option value="en_fr">EN-FR</option>
                <option value="en_tr">EN-TR</option>
                <option value="en_ru">EN-RU</option>
              </select>
            </>
          }
        />
        <div className="justify-center items-center flex gap-2 mb-2">
          <Link
            to={`/release-book/${bookId}?plang=${pageLanguage}`}
            className="bg-white shadow px-4 py-1 rounded hover:bg-blue-600 flex items-center gap-2"
          >
            Release Book
          </Link>
        </div>

        {loading && <p className="text-center text-gray-500">Loading...</p>}
        {error && <p className="text-center text-red-500">{error}</p>}

        {!loading && pages.length === 0 && (
          <p className="text-center text-gray-500">No pages available.</p>
        )}

        <div className="flex flex-wrap justify-center gap-4">
          {pages?.map((page) => (
            <div
              key={page._id}
              className="border rounded-lg shadow p-4 flex flex-col items-center bg-white w-[200px]"
            >
              <h3 className="text-sm font-semibold mb-2 text-center">
                {page.page_number}.{page.name}
              </h3>

              {page['image_preview_' + pageLanguage] ? (
                <img
                  src={getImageUrl(page['image_preview_' + pageLanguage])}
                  alt={`Page render ${pageLanguage}`}
                  className="w-full h-auto object-contain mb-2 cursor-pointer"
                  onClick={() =>
                    window.open(
                      `${getImageUrl(page['image_preview_' + pageLanguage])}`,
                      '_blank',
                    )
                  }
                />
              ) : (
                <img
                  src={`/uploads/${page.image_path}`}
                  alt="Original page artwork"
                  className="w-full h-auto object-contain mb-2 cursor-pointer"
                  onClick={() =>
                    window.open(`/uploads/${page.image_path}`, '_blank')
                  }
                />
              )}

              <div className="mt-2 flex flex-wrap justify-center gap-2">
                <Link
                  to={`/edit/${page._id}?plang=${pageLanguage}`}
                  className="bg-white border px-3 py-1 rounded hover:bg-gray-100 text-sm flex items-center gap-1"
                >
                  <Edit size={16} /> Edit
                </Link>
                <button
                  onClick={() => handleDuplicatePage(page._id)}
                  className="bg-white border px-3 py-1 rounded hover:bg-gray-100 text-sm"
                >
                  Duplicate
                </button>
                <button
                  onClick={() => handleRenamePagePrompt(page._id, page.name)}
                  className="bg-white border px-3 py-1 rounded hover:bg-gray-100 text-sm"
                >
                  Rename
                </button>
                <button
                  onClick={() => handleDelete(page._id)}
                  className="bg-white border px-3 py-1 rounded hover:bg-gray-100 text-sm flex items-center gap-1"
                >
                  <Trash size={16} /> Delete
                </button>
                <button
                  onClick={() =>
                    handleMovePagePrompt(page._id, page.page_number)
                  }
                  className="bg-white border px-3 py-1 rounded hover:bg-gray-100 text-sm"
                >
                  Move
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default PreviewBook;
